<template>
  <v-card>
    <div class="performance-snapshot mb-2">
      <div class="d-flex align-center justify-space-between mb-4">
        <h2 class="text-secondary">{{ title }}</h2>
        <v-btn variant="text" density="comfortable" color="primary">
          What is this?
        </v-btn>
      </div>

      <!-- Performance Grid -->
      <v-table density="compact" class="performance-table" elevation="0">
        <thead>
          <tr>
            <th class="text-left" style="width: 120px"></th>
            <th
              v-for="grade in ['K', '1', '2', '3', '4', '5', '6']"
              :key="grade"
              class="text-center text-secondary font-weight-bold"
              style="position: relative"
            >
              <div class="th-wrapper">
                <span class="cell-content">
                  {{ grade }}
                </span>
                <div
                  v-if="currentGrade === grade"
                  class="active-grade-indicator"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(gateway, index) in ['Gateway 1', 'Gateway 2', 'Gateway 3']"
            :key="gateway"
          >
            <td class="text-left text-secondary font-weight-bold">
              {{ gateway }}
            </td>
            <td
              v-for="(status, gradeIndex) in getGatewayStatuses(index)"
              :key="gradeIndex"
              class="text-center py-4"
            >
              <v-icon
                :color="getStatusColor(status)"
                icon="mdi-circle"
                size="small"
              ></v-icon>
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- Legend -->
      <div class="d-flex align-center mt-6 legend">
        <div class="text-caption mr-6">Key:</div>
        <div class="d-flex align-center mr-6">
          <v-icon
            color="status-green"
            size="small"
            icon="mdi-circle"
            class="mr-2"
          ></v-icon>
          <span class="text-caption">Meets Expectations</span>
        </div>
        <div class="d-flex align-center mr-6">
          <v-icon
            color="status-yellow"
            size="small"
            icon="mdi-circle"
            class="mr-2"
          ></v-icon>
          <span class="text-caption">Partially Meets Expectations</span>
        </div>
        <div class="d-flex align-center mr-6">
          <v-icon
            color="status-red"
            size="small"
            icon="mdi-circle"
            class="mr-2"
          ></v-icon>
          <span class="text-caption">Does Not Meet Expectations</span>
        </div>
        <div class="d-flex align-center">
          <v-icon
            color="status-gray"
            size="small"
            icon="mdi-circle"
            class="mr-2"
          ></v-icon>
          <span class="text-caption">Not Eligible</span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';

  const route = useRoute();

  const currentGrade = computed(() => {
    const gradeParam = route.params.grade;
    if (typeof gradeParam !== 'string') return null;

    const gradeSlug = gradeParam.toLowerCase(); // Normalize to lowercase

    // Map word-based slugs first
    const gradeMap: Record<string, string> = {
      kindergarten: 'K',
      first: '1',
      second: '2',
      third: '3',
      fourth: '4',
      fifth: '5',
      sixth: '6',
      // Add more if needed (seventh, eighth, etc.)
    };

    // Extract the potential grade word (e.g., "first" from "first-grade")
    const gradeWord = gradeSlug.split('-')[0];

    if (gradeMap[gradeWord]) {
      return gradeMap[gradeWord]; // Return 'K', '1', '2', etc.
    }

    // Handle numeric slugs like 'k', '1', '2'
    if (gradeSlug === 'k') return 'K';
    if (/^\d+$/.test(gradeSlug)) {
      return gradeSlug;
    }

    return null; // Return null if no match found or format is unexpected
  });

  withDefaults(
    defineProps<{
      title?: string;
    }>(),
    {
      title: 'Series Performance Snapshot',
    },
  );

  function getGatewayStatuses(gatewayIndex: number) {
    // This is mock data matching the image - replace with actual data from your API
    const statusMatrix = [
      ['red', 'yellow', 'green', 'green', 'yellow', 'yellow', 'green'], // Gateway 1
      ['red', 'yellow', 'green', 'green', 'yellow', 'yellow', 'green'], // Gateway 2
      ['gray', 'yellow', 'green', 'yellow', 'yellow', 'green', 'yellow'], // Gateway 3
    ];
    return statusMatrix[gatewayIndex];
  }

  function getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      green: 'status-green',
      yellow: 'status-yellow',
      red: 'status-red',
      gray: 'status-gray',
    };
    return colorMap[status] || 'grey';
  }
</script>

<style lang="scss" scoped>
  .performance-snapshot {
    border-radius: 8px;
    padding: 24px;
    box-shadow: none;
  }

  .performance-table {
    border: none;
    border-radius: 3px;
    background: white;
    :deep(.v-table__wrapper) {
      overflow: visible !important;
    }

    :deep(th) {
      position: relative;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.87);
      background: white;
      .th-wrapper {
        position: relative;
        .cell-content {
          position: relative;
          z-index: 1;
        }
        .active-grade-indicator {
          z-index: 1;
          position: absolute;
          top: -15px;
          left: 0;
          width: 100%;
          height: 220px;
          background: rgba(var(--v-theme-primary), 0.05);
          border-radius: 3px;
          border: 2px solid rgba(var(--v-theme-primary), 0.5);
        }
      }
    }

    :deep(td) {
      background: white;
      .v-icon {
        margin: 0 !important;
      }
    }

    :deep(tr) {
      background: white;

      &:last-child td {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }
    }
  }

  .legend {
    color: rgba(0, 0, 0, 0.6);
  }
</style>
